import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { publicGet } from "lib/apiClient";

const POLL_MS = 30000;

const FALLBACK = { status: null, error: null, refresh: () => Promise.resolve(), marketCreation: null, maintenance: null };

const PlatformStatusContext = createContext(FALLBACK);

export function PlatformStatusProvider({ children }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const next = await publicGet("/public/platform-status");
      setStatus(next);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not read the platform status.");
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({
      status,
      error,
      refresh,
      marketCreation: status?.marketCreation ?? null,
      maintenance: status?.maintenance ?? null,
    }),
    [status, error, refresh],
  );

  return <PlatformStatusContext.Provider value={value}>{children}</PlatformStatusContext.Provider>;
}

export function usePlatformStatus() {
  return useContext(PlatformStatusContext);
}
